import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { setTokens } from '../services/authService';

/**
 * AuthCallback — Handles OAuth/SSO redirect with JWT tokens.
 *
 * After Google/LinkedIn/SSO login, the FastAPI backend redirects to:
 *     /auth/callback?access_token=...&refresh_token=...
 *
 * This component:
 *   1. Extracts tokens from URL query params
 *   2. Stores them via authService
 *   3. Redirects to /upload (the main authenticated page)
 *
 * Replaces Flask's pattern where the OAuth callback set a session cookie
 * and redirected to /login?login=success.
 */
function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(null);

  useEffect(() => {
    const accessToken = searchParams.get('access_token') || '';
    const refreshToken = searchParams.get('refresh_token') || '';
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setError(`Authentication failed: ${errorParam}`);
      setTimeout(() => navigate('/login?error=oauth_failed'), 2000);
      return;
    }

    const hash = window.location.hash || '';
    const hashParams = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
    const accessFromHash = hashParams.get('access_token') || '';
    const refreshFromHash = hashParams.get('refresh_token') || '';

    const finalAccess = accessToken || accessFromHash;
    const finalRefresh = refreshToken || refreshFromHash;

    if (finalAccess && finalRefresh) {
      // Store the tokens
      setTokens({ access_token: finalAccess, refresh_token: finalRefresh });

      // Scrub tokens from the URL (avoid leaking via history/referrers/screenshots)
      try {
        window.history.replaceState({}, document.title, '/auth/callback');
      } catch {
        // ignore
      }

      // Redirect to the main app
      navigate('/upload', { replace: true });
    } else {
      setError('Missing authentication tokens. Please try again.');
      setTimeout(() => navigate('/login?error=oauth_failed'), 2000);
    }
  }, [navigate, searchParams]);

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        flexDirection: 'column',
        gap: '1rem',
        color: 'var(--text-color, #333)',
      }}>
        <div style={{ fontSize: '18px', color: '#e53e3e' }} role="alert">
          {error}
        </div>
        <div style={{ fontSize: '14px', color: '#888' }}>Redirecting to login...</div>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      flexDirection: 'column',
      gap: '1rem',
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '4px solid rgba(0, 170, 255, 0.2)',
        borderTop: '4px solid var(--primary-color, #00aaff)',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
      }}></div>
      <div style={{ color: 'var(--text-color, #333)', fontSize: '14px' }}>
        Completing authentication...
      </div>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default AuthCallback;



